'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { createBrowserClient } from '@/lib/supabase-clients';

interface CreateDomainModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateDomainModal({ open, onOpenChange }: CreateDomainModalProps) {
  const router = useRouter();
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    handle: '',
    description: '',
  });

  const validateHandle = (handle: string) => {
    return /^[a-z0-9_]+$/.test(handle);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Domain name is required');
      return;
    }

    if (!formData.handle.trim()) {
      toast.error('Domain handle is required');
      return;
    }

    if (!validateHandle(formData.handle)) {
      toast.error('Handle must contain only lowercase letters, numbers, and underscores');
      return;
    }

    setIsCreating(true);

    try {
      const response = await fetch('/api/domains/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          handle: formData.handle.trim(),
          description: formData.description.trim(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create domain');
      }

      const { domain } = await response.json();
      
      toast.success('Domain created successfully!');
      onOpenChange(false);
      
      // Switch to the new domain
      const switchResponse = await fetch('/api/domains/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domainId: domain.id }),
      });

      if (switchResponse.ok) {
        router.push('/dashboard/settings/general');
      } else {
        router.refresh();
      }
    } catch (error: any) {
      console.error('Error creating domain:', error);
      toast.error(error.message || 'Failed to create domain');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Domain</DialogTitle>
            <DialogDescription>
              Create a new domain to organize your content and team members.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Domain Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Domain"
                disabled={isCreating}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="handle">Handle</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">@</span>
                <Input
                  id="handle"
                  value={formData.handle}
                  onChange={(e) => setFormData({ ...formData, handle: e.target.value.toLowerCase() })}
                  placeholder="mydomain"
                  disabled={isCreating}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Only lowercase letters, numbers, and underscores
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tell people what your domain is about..."
                rows={3}
                disabled={isCreating}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Domain'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}